/* ============================================================
   Financeability scoring.

   The evidenced problem: 68% of Kenyan SMEs turned down or delayed a
   confirmed contract in the prior twelve months for want of upfront
   capital, against a backdrop of KSh 465.87bn in public pending bills.
   The constraint is rarely demand. It is the gap between paying duty on
   arrival and being paid 60–90 days later.

   This module quantifies that gap and scores how a lender is likely to
   read the operation. It is emphatically NOT a credit decision and does
   not pretend to be one — it is the arithmetic an operator should be
   able to present before walking into the meeting.
   ============================================================ */

export const DSCR_BANDS = [
  { min: 1.5, label: 'Comfortable', tone: 'var(--ca-positive)', note: 'Most lenders will engage on these terms.' },
  { min: 1.25, label: 'Acceptable', tone: 'var(--ca-chart-2)', note: 'Above the covenant most term sheets set.' },
  { min: 1.0, label: 'Tight', tone: 'var(--ca-warning)', note: 'Servicing debt, but with no headroom for a bad month.' },
  { min: -Infinity, label: 'Distressed', tone: 'var(--ca-danger)', note: 'Earnings do not cover debt service.' }
];

/**
 * No debt to service is the strongest coverage position there is, not the
 * weakest. DSCR is undefined rather than bad, so it gets its own band —
 * previously an operator with zero debt fell through the !isFinite guard
 * to "Distressed · Earnings do not cover debt service", which is the
 * opposite of true.
 */
export const NO_DEBT_BAND = {
  min: Infinity,
  label: 'No debt service',
  tone: 'var(--ca-positive)',
  note: 'Nothing to cover. Coverage is not the constraint here.'
};

export function dscrBand(dscr) {
  if (dscr === Infinity) return NO_DEBT_BAND;
  if (!Number.isFinite(dscr)) return DSCR_BANDS[DSCR_BANDS.length - 1];
  return DSCR_BANDS.find((b) => dscr >= b.min);
}

/**
 * Cash conversion cycle for a freight operation.
 *
 * DSO + duty float − DPO. Duty float is the piece a generic CCC formula
 * misses and the piece that actually hurts here: duty is payable the
 * moment cargo lands, long before the customer settles.
 */
export function cashCycle({ dso, dpo, dutyFloatDays }) {
  return (dso || 0) + (dutyFloatDays || 0) - (dpo || 0);
}

/**
 * Cash locked in the cycle. Approximated as daily revenue × CCC.
 *
 * This overstates slightly for a business with thin margins, because
 * only the cost portion is genuinely funded — but it is the figure a
 * lender will reach for first, and understating a funding gap is the
 * more expensive error.
 */
export function workingCapitalGap({ annualRevenue, ccc }) {
  if (!Number.isFinite(annualRevenue) || annualRevenue <= 0) return 0;
  return (annualRevenue / 365) * ccc;
}

/* ---- Dimension scoring, 0–100 each ---- */

function scoreDscr(dscr) {
  // Infinity means annualDebtService is zero. That is full marks, not
  // nil: there is no debt service to fail to cover. Scoring it 0 dragged
  // 30 weighted points off every debt-free operator and then sorted DSCR
  // to the top of the weakest-dimension list, so the tool advised them to
  // refinance debt they do not have.
  if (dscr === Infinity) return 100;
  if (!Number.isFinite(dscr) || dscr <= 0) return 0;
  if (dscr >= 2) return 100;
  if (dscr <= 0.8) return 0;
  return Math.round(((dscr - 0.8) / 1.2) * 100);
}

function scoreCycle(ccc) {
  // 30 days or better is strong; beyond 120 the operation is financing
  // its customers out of its own pocket.
  if (ccc <= 30) return 100;
  if (ccc >= 120) return 0;
  return Math.round(((120 - ccc) / 90) * 100);
}

function scoreConcentration(topClientShare) {
  // Above 50% from one client, a lender prices the customer's risk, not yours.
  if (!Number.isFinite(topClientShare)) return 50;
  if (topClientShare <= 20) return 100;
  if (topClientShare >= 70) return 0;
  return Math.round(((70 - topClientShare) / 50) * 100);
}

function scoreFleetAge(averageAge) {
  if (!Number.isFinite(averageAge)) return 50;
  if (averageAge <= 5) return 100;
  if (averageAge >= 15) return 0;
  return Math.round(((15 - averageAge) / 10) * 100);
}

function scoreRecords({ auditedAccounts, managementAccounts, assetRegister, complianceCurrent }) {
  let score = 0;
  if (auditedAccounts) score += 40;
  if (managementAccounts) score += 25;
  if (assetRegister) score += 20;
  if (complianceCurrent) score += 15;
  return score;
}

export const DIMENSIONS = [
  { id: 'dscr', label: 'Debt service coverage', weight: 30 },
  { id: 'cycle', label: 'Cash conversion cycle', weight: 25 },
  { id: 'concentration', label: 'Customer concentration', weight: 15 },
  { id: 'fleet', label: 'Asset quality', weight: 15 },
  { id: 'records', label: 'Records & compliance', weight: 15 }
];

export function assessFinanceability(input) {
  const ccc = cashCycle(input);
  const gap = workingCapitalGap({ annualRevenue: input.annualRevenue, ccc });
  const dscr =
    input.annualDebtService > 0 ? input.ebitda / input.annualDebtService : input.ebitda > 0 ? Infinity : 0;

  const scores = {
    dscr: scoreDscr(dscr),
    cycle: scoreCycle(ccc),
    concentration: scoreConcentration(input.topClientShare),
    fleet: scoreFleetAge(input.fleetAge),
    records: scoreRecords(input)
  };

  const total = DIMENSIONS.reduce((sum, d) => sum + scores[d.id] * d.weight, 0) / 100;

  return {
    ccc,
    gap,
    dscr,
    dscrBand: dscrBand(dscr),
    scores,
    dimensions: DIMENSIONS.map((d) => ({ ...d, score: scores[d.id] })).sort(
      (a, b) => a.score - b.score
    ),
    score: Math.round(total),
    verdict: verdictFor(Math.round(total))
  };
}

function verdictFor(score) {
  if (score >= 75)
    return {
      label: 'Bankable',
      tone: 'var(--ca-positive)',
      note: 'The arithmetic supports a conversation with a commercial lender.'
    };
  if (score >= 55)
    return {
      label: 'Fixable',
      tone: 'var(--ca-warning)',
      note: 'Close the weakest dimension below and the picture changes materially.'
    };
  return {
    label: 'Not yet fundable',
    tone: 'var(--ca-danger)',
    note: 'Address the fundamentals before approaching a lender — a declined application is remembered.'
  };
}

/** What a lender will ask for, in the order they will ask. */
export const LENDER_CHECKLIST = [
  { id: 'auditedAccounts', label: 'Audited accounts, two years', weight: 'Usually mandatory' },
  { id: 'managementAccounts', label: 'Management accounts, current year to date', weight: 'Usually mandatory' },
  { id: 'assetRegister', label: 'Asset register with valuations', weight: 'Required for asset finance' },
  { id: 'complianceCurrent', label: 'All licences and tax compliance current', weight: 'Screening condition' }
];

/** Targeted advice, keyed to the weakest dimension. */
export const REMEDIES = {
  dscr:
    'Debt service is the binding constraint. Refinancing to a longer tenor lowers the annual ' +
    'payment without changing the principal, and is usually easier to negotiate than a rate cut. ' +
    'Failing that, the coverage has to come from margin, not volume.',
  cycle:
    'Cash is trapped in the cycle. Attack the duty float first — a KRA duty credit facility ' +
    'moves the largest single outflow off day zero. Then invoice on delivery rather than ' +
    'month-end, and price late payment explicitly into contracts rather than absorbing it.',
  concentration:
    'One customer carries too much of the book, so a lender prices their risk rather than yours. ' +
    'Winning two mid-sized accounts changes the credit assessment more than growing the largest one.',
  fleet:
    'The fleet is ageing into a maintenance cost curve that lenders can read. A funded, dated ' +
    'replacement plan is worth more in a credit paper than the fleet condition itself.',
  records:
    'The numbers may be fine but they are not presentable. Audited accounts and a valued asset ' +
    'register are cheap relative to the cost of a declined application, and they are the first ' +
    'thing asked for.'
};
